package songwritersnotebook.app.utils

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.asDeferred
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

actual class AuthCredentials

actual class AuthHandler actual constructor() {

    private val auth = Firebase.auth
    private val scope = CoroutineScope(Dispatchers.Main)

    private val _isAuthLoading = MutableStateFlow(true)
    actual val isAuthLoading: kotlinx.coroutines.flow.StateFlow<Boolean> = _isAuthLoading.asStateFlow()

    actual val authState: Flow<FirebaseUser?> = auth.authStateChanged.onEach { user ->
        _isAuthLoading.value = false
        // Whenever auth changes, ensure the global bridge is synced
        if (user != null) {
            scope.launch {
                try {
                    val token = user.getIdToken(false)
                    js("window.snBridge.syncSession(token)")
                } catch (e: Exception) {
                    println("AuthHandler: Failed to sync token to bridge: $e")
                }
            }
        }
    }

    actual fun getCurrentUser(): FirebaseUser? = auth.currentUser

    actual suspend fun signOut() {
        try {
            auth.signOut()
        } catch (e: Exception) {
            println("Error signing out: $e")
        }
    }

    actual fun getSignInIntent(): Any? = null

    actual suspend fun firebaseAuthWithGoogle(credentials: AuthCredentials): FirebaseUser? {
        return try {
            // We use the JS bridge to perform the actual popup login
            // to avoid Kotlin compiler issues with Firebase dynamic types.
            val promise = SnBridge.doGoogleLogin()
            (promise as kotlin.js.Promise<*>).asDeferred().await()
            auth.currentUser
        } catch (e: Exception) {
            val errorStr = e.toString()
            if (errorStr.contains("auth/popup-closed-by-user") || errorStr.contains("auth/cancelled-popup-request") || e is kotlinx.coroutines.CancellationException) {
                // Expected cancellation
                println("AuthHandler: User cancelled sign-in.")
            } else {
                println("Firebase auth failed: $e")
            }
            null
        }
    }

    actual suspend fun signInWithEmail(email: String, password: String): FirebaseUser? {
        return try {
            val promise = js("window.snBridge.doEmailLogin(email, password)")
            (promise as kotlin.js.Promise<*>).asDeferred().await()
            auth.currentUser
        } catch (e: Throwable) {
            val errorMsg = e.toString()
            println("AuthHandler: Sign in with email failed: $errorMsg")
            throw Exception(errorMsg)
        }
    }

    actual suspend fun signUpWithEmail(email: String, password: String): FirebaseUser? {
        return try {
            val promise = js("window.snBridge.doEmailSignup(email, password)")
            (promise as kotlin.js.Promise<*>).asDeferred().await()
            auth.currentUser
        } catch (e: Throwable) {
            val errorMsg = e.toString()
            println("AuthHandler: Sign up with email failed: $errorMsg")
            throw Exception(errorMsg)
        }
    }

    actual suspend fun deleteUser(): Boolean {
        return try {
            auth.currentUser?.delete()
            true
        } catch (e: Exception) {
            println("AuthHandler: Delete user failed: $e")
            throw e
        }
    }

    actual suspend fun sendPasswordResetEmail(email: String) {
        try {
            val promise = js("window.snBridge.doPasswordReset(email)")
            (promise as kotlin.js.Promise<*>).asDeferred().await()
        } catch (e: Throwable) {
            val errorMsg = e.toString()
            println("AuthHandler: Send password reset failed: $errorMsg")
            throw Exception(errorMsg)
        }
    }
}
